import {
    alpha,
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography
} from '@mui/material';

import dayjs from 'dayjs';
import ContentContainer from '@libComponents/ContentContainer';
import Pagination from '@libComponents/Pagination';
import RowsPerPage from '@libComponents/RowsPerPage';
import { theme } from 'app/Theme';

import TableTopHeader from './TableTopHeader';
import TableFilterBar from './TableFilterBar';

/**
 * CommonDataTable
 *
 * A universal, configurable table component that standardises the header
 * structure, filter bar, column definitions, row rendering, sorting, and
 * pagination across all screens in eHRMS.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * COLUMN DEFINITION SCHEMA
 * ──────────────────────────────────────────────────────────────────────────
 * @typedef {Object} ColumnConfig
 * @property {string}   id           - Data-field key (e.g. 'fullName', 'status').
 * @property {string}   label        - Column header label.
 * @property {boolean}  [sortable]   - Enable click-to-sort on this column.
 * @property {'left'|'center'|'right'} [align] - Cell alignment (default 'left').
 * @property {number|string} [minWidth] - Minimum column width.
 * @property {boolean}  [isVisible]  - Initial visibility (used with BasicMenu). Default true.
 * @property {boolean}  [disabledChange] - Prevent toggling visibility. Default false.
 * @property {'string'|'number'|'date'|'custom'} [type] - Field data type (used for default formatting).
 * @property {(value: any, row: Object, index: number) => React.ReactNode} [render]
 *   Custom cell renderer. When absent, falls back to type-based formatting.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * FILTER CONFIG SCHEMA
 * ──────────────────────────────────────────────────────────────────────────
 * @typedef {Object} FilterConfig
 * @property {string}      id        - Unique filter key.
 * @property {string}      label     - Input label.
 * @property {string}      [placeholder]
 * @property {Array}       options   - Array of { value, label }.
 * @property {Object|null} value     - Currently selected option (or null).
 * @property {Function}    onChange  - Called with selected option (or null).
 * @property {number}      [minWidth]
 *
 * ──────────────────────────────────────────────────────────────────────────
 * PROPS
 * ──────────────────────────────────────────────────────────────────────────
 * @param {Object}           props
 *
 * // Data
 * @param {ColumnConfig[]}   props.columns           - Column definitions.
 * @param {Object[]}         props.data              - Row data array.
 * @param {number}           [props.totalRows]       - Total matching rows (for pagination).
 * @param {boolean}          [props.loading]         - Show loading spinner overlay.
 *
 * // Header
 * @param {string}           props.title             - Page title (left side of header).
 * @param {string}           [props.searchName]      - Name attribute for the search input.
 * @param {string}           [props.searchPlaceholder]
 * @param {string}           [props.searchValue]     - Controlled search string.
 * @param {Function}         [props.onSearch]        - Called with new search string.
 * @param {React.Node}       [props.primaryAction]   - Primary button (e.g. "Create Client").
 * @param {React.Node}       [props.extraActions]    - Extra icon buttons in the header bar.
 * @param {Object}           [props.tableData]       - Column visibility map (for BasicMenu).
 * @param {Function}         [props.setTableData]    - Visibility setter (for BasicMenu).
 * @param {boolean}          [props.showColumnMenu]  - Whether to show column-visibility menu. Default false.
 *
 * // Filters
 * @param {FilterConfig[]}   [props.filters]         - Typeable filter configurations.
 * @param {Function}         [props.onResetFilters]  - Reset-all-filters callback.
 *
 * @param {React.Node}       [props.extraFiltersContent] - Custom async filter components passed to TableFilterBar.
 *
 * // Sorting
 * @param {string|null}      [props.sortField]       - Currently sorted field id.
 * @param {boolean}          [props.sortDescending]  - True when sorted descending.
 * @param {Function}         [props.onSort]          - Called with column id when header clicked.
 *
 * // Pagination
 * @param {number}           [props.page]            - Current page (1-indexed).
 * @param {number}           [props.pageSize]        - Rows per page.
 * @param {number[]}         [props.pageSizeOptions] - Available rows-per-page options.
 * @param {string}           [props.pageSizeName]    - Name for the RowsPerPage dropdown.
 * @param {Function}         [props.onPageChange]    - (_, newPage) => void.
 * @param {Function}         [props.onRowsPerPageChange] - (value) => void.
 * @param {boolean}          [props.showPagination]  - Whether to render pagination. Default true.
 *
 * // Row customisation
 * @param {Function}         [props.renderRow]       - (row, index) => <TableRow>. When provided,
 *   overrides the built-in default row renderer. Use for complex rows (chips, menus, etc.).
 * @param {React.Node}       [props.emptyState]      - Content shown when data is empty.
 *
 * // Extra footer content (e.g. dialogs)
 * @param {React.Node}       [props.children]        - Extra children appended after the table.
 */
const CommonDataTable = ({
    // Data
    columns = [],
    data = [],
    totalRows = 0,
    loading = false,

    // Header
    title,
    searchName,
    searchPlaceholder,
    searchValue,
    onSearch,
    primaryAction,
    extraActions,
    tableData,
    setTableData,
    showColumnMenu = false,

    // Filters
    filters = [],
    onResetFilters,
    extraFiltersContent,

    // Sorting
    sortField,
    sortDescending,
    onSort,

    // Pagination
    page = 1,
    pageSize = 10,
    pageSizeOptions,
    pageSizeName = 'common-table',
    onPageChange,
    onRowsPerPageChange,
    showPagination = true,

    // Row customisation
    renderRow,
    emptyState,

    // Extra
    children
}) => {
    // Compute visible columns
    const visibleColumns = tableData
        ? columns.filter((col) => {
              const entry = tableData[col.id];
              return entry ? entry.isVisible !== false : true;
          })
        : columns;

    // Default cell renderer
    const renderCell = (col, value, row, idx) => {
        if (col.render) return col.render(value, row, idx);
        if (col.type === 'date') {
            return value ? dayjs(value).format('YYYY-MM-DD') : '';
        }
        return value ?? '';
    };

    // Default row renderer (used when renderRow is NOT provided)
    const defaultRenderRow = (row, idx) => (
        <TableRow key={idx} hover sx={{ '&:last-child td': { border: 0 } }}>
            {visibleColumns.map((col) => (
                <TableCell key={col.id} align={col.align || 'left'}>
                    {renderCell(col, row[col.id], row, idx)}
                </TableCell>
            ))}
        </TableRow>
    );

    const headerControls = (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TableTopHeader
                title={title}
                searchName={searchName}
                searchPlaceholder={searchPlaceholder}
                searchValue={searchValue}
                onSearch={onSearch}
                primaryAction={primaryAction}
                extraActions={extraActions}
                tableData={tableData}
                setTableData={setTableData}
                showColumnMenu={showColumnMenu}
            />
            {(filters.length > 0 || extraFiltersContent) && (
                <TableFilterBar filters={filters} onResetFilters={onResetFilters} extraFiltersContent={extraFiltersContent} />
            )}
        </Box>
    );

    const rowsPerPageData = pageSizeOptions
        ? pageSizeOptions.map((o) => ({ label: `${o}`, value: o }))
        : undefined;

    return (
        <ContentContainer
            title=''
            headerBg={theme.palette.background.mainHeader}
            AdornmentComponent={headerControls}
        >
            {/* Table */}
            <Box
                sx={{
                    width: '100%',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    WebkitOverflowScrolling: 'touch',
                    minWidth: 0,
                    position: 'relative',
                    '& table': {
                        tableLayout: { xs: 'auto', sm: 'fixed' },
                        minWidth: 'max-content'
                    }
                }}
            >
                {/* Loading overlay */}
                {loading && (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.6),
                            zIndex: 10
                        }}
                    >
                        <CircularProgress size={36} />
                    </Box>
                )}

                <TableContainer sx={{ width: '100%', overflowX: 'auto', overflowY: 'hidden' }}>
                    <Table sx={{ minWidth: 'max-content' }}>
                        <TableHead>
                            <TableRow>
                                {visibleColumns.map((col) => (
                                    <TableCell
                                        key={col.id}
                                        align={col.align || 'left'}
                                        style={{ minWidth: col.minWidth }}
                                        sx={{ fontWeight: 'bold' }}
                                    >
                                        {col.sortable && onSort ? (
                                            <TableSortLabel
                                                active={sortField === col.id}
                                                direction={
                                                    sortField === col.id && sortDescending
                                                        ? 'desc'
                                                        : 'asc'
                                                }
                                                onClick={() => onSort(col.id)}
                                            >
                                                {col.label}
                                            </TableSortLabel>
                                        ) : (
                                            col.label
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {data.length === 0 && !loading ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={visibleColumns.length || 1}
                                        align='center'
                                        sx={{ py: 4 }}
                                    >
                                        {emptyState || (
                                            <Typography variant='body1' color='text.secondary'>
                                                No records found.
                                            </Typography>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                data.map((row, idx) =>
                                    renderRow ? renderRow(row, idx) : defaultRenderRow(row, idx)
                                )
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Pagination */}
            {showPagination && (onPageChange || onRowsPerPageChange) && (
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        columnGap: 2,
                        rowGap: 1,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        px: 1,
                        pt: 0.5,
                        pb: 1,
                        '@media screen and (max-width: 700px)': { justifyContent: 'center' }
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 1
                        }}
                    >
                        {onRowsPerPageChange && (
                            <RowsPerPage
                                input={{
                                    name: pageSizeName,
                                    onChange: onRowsPerPageChange,
                                    value: pageSize
                                }}
                                data={rowsPerPageData}
                            />
                        )}
                        <Typography
                            variant='body2'
                            color='text.secondary'
                            sx={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}
                        >
                            ( Total : <strong>{totalRows}</strong> )
                        </Typography>
                    </Box>

                    {onPageChange && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                flexWrap: 'wrap',
                                gap: 1,
                                ml: 'auto',
                                '@media screen and (max-width: 700px)': { ml: 0, justifyContent: 'center' }
                            }}
                        >
                            <Pagination
                                count={Math.max(1, Math.ceil(totalRows / pageSize))}
                                onChange={onPageChange}
                                page={page}
                                sx={{ display: 'flex', justifyContent: 'center' }}
                            />
                        </Box>
                    )}
                </Box>
            )}

            {/* Extra children (dialogs, etc.) */}
            {children}
        </ContentContainer>
    );
};

export default CommonDataTable;
